import { useCallback, useEffect, useRef, useState } from 'react';
import { consultApi } from '../utils/consultApi';
import { useRealtimeCall } from './useRealtimeCall';

const COVERAGE_DEBOUNCE_MS = 250;

// Drives the /consult page: session, messages, the selected map location,
// and (Phase 3) the realtime call lifecycle.
export function useConsult() {
  const [messages, setMessages] = useState([]);
  const [location, setLocationState] = useState(null); // { lat, lng } | null
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [registrationUrl, setRegistrationUrl] = useState(null);
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [coverage, setCoverage] = useState(null);
  const [coverageError, setCoverageError] = useState(null);
  const [coverageRefresh, setCoverageRefresh] = useState(0);

  const coverageRequest = useRef(0);
  const initStarted = useRef(false);

  // Invalidate an in-flight lookup as soon as a newer point is selected. The
  // effect below also checks this sequence before committing a response.
  const setLocation = useCallback((nextLocation) => {
    coverageRequest.current += 1;
    setLocationState(nextLocation);
    setCoverageLoading(Boolean(nextLocation));
    setCoverage(null);
    setCoverageError(null);
  }, []);

  // Phase 3: realtime voice - the hook owns its own state machine.
  const call = useRealtimeCall(location);

  useEffect(() => {
    if (initStarted.current) return;
    initStarted.current = true;
    (async () => {
      try {
        await consultApi.startSession();
        const msgs = await consultApi.getMessages();
        setMessages(msgs.messages || []);
      } catch (e) {
        setError(e.message);
      } finally {
        setReady(true);
      }
    })();
  }, []);

  useEffect(() => {
    const requestId = ++coverageRequest.current;

    if (!location) {
      setCoverageLoading(false);
      setCoverage(null);
      setCoverageError(null);
      return undefined;
    }

    setCoverageLoading(true);
    setCoverage(null);
    setCoverageError(null);

    const timer = setTimeout(async () => {
      try {
        const response = await consultApi.getCoverageOffers(location);
        if (coverageRequest.current !== requestId) return;
        if (!response || !Array.isArray(response.carriers)) {
          throw new Error('پاسخ بررسی پوشش معتبر نبود.');
        }
        setCoverage(response);
        setCoverageError(null);
      } catch (e) {
        if (coverageRequest.current !== requestId) return;
        setCoverage(null);
        setCoverageError(e?.message || 'بررسی پوشش ناموفق بود.');
      } finally {
        if (coverageRequest.current === requestId) setCoverageLoading(false);
      }
    }, COVERAGE_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      if (coverageRequest.current === requestId) coverageRequest.current += 1;
    };
  }, [location, coverageRefresh]);

  // When a call ends, refresh the message list so newly-logged voice
  // transcripts (written by the backend's sideband) appear in the chat log.
  useEffect(() => {
    if (call.state === 'idle' && ready) {
      consultApi.getMessages().then((r) => setMessages(r.messages || [])).catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [call.state]);

  // Poll for a registration link while a voice call is active (the server
  // cannot push to the browser over WebRTC, so we pull). Also do one fetch
  // when the call transitions to idle to catch a link offered at the very end.
  useEffect(() => {
    if (call.state === 'active') {
      const id = setInterval(() => {
        consultApi.getRegistrationLink().then((url) => {
          if (url) setRegistrationUrl(url);
        }).catch(() => {});
      }, 4000);
      return () => clearInterval(id);
    }
    if (call.state === 'idle' && ready) {
      consultApi.getRegistrationLink().then((url) => {
        if (url) setRegistrationUrl(url);
      }).catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [call.state, ready]);

  const sendMessage = useCallback(async (content) => {
    setBusy(true); setError(null);
    setMessages((m) => [...m, { id: `tmp-${Date.now()}`, role: 'customer', content }]);
    try {
      const resp = await consultApi.sendMessage(content, location);
      setMessages((m) => [...m, { id: `tmp-a-${Date.now()}`, role: 'agent', content: resp.reply }]);
      if (resp.registration_url) setRegistrationUrl(resp.registration_url);
    } catch (e) {
      setError(e.message);
      setMessages((m) => [...m, { id: `tmp-e-${Date.now()}`, role: 'agent', content: `خطا: ${e.message}` }]);
    } finally {
      setBusy(false);
    }
  }, [location]);

  // Explicit "اشتراک موقعیت" button. Branches by surface:
  //   * Active voice call → push the location to the agent over the open
  //     transport (DC for OpenAI / control frame for Gemini) so the model
  //     re-runs check_coverage in-flight.
  //   * Text chat (or idle) → send a short customer message announcing the
  //     share; the existing sendMessage carries `location` to the API and
  //     consultAgent.js bakes it into the system prompt for this turn,
  //     so the agent has the coords AND a clear intent ("please check
  //     this point").
  // In both cases the agent ends up calling check_coverage on the pinned
  // point - UX symmetry between text and voice surfaces.
  const shareLocation = useCallback(() => {
    if (!location) return false;
    if (call.state === 'active') {
      return call.shareLocation(location);
    }
    // Chat / idle path. Fire-and-forget; sendMessage already shows a
    // customer bubble + waits for the agent reply asynchronously.
    // No tool name in the customer-visible message - the system prompt
    // already mandates check_coverage on any shared point.
    sendMessage('موقعیتم رو روی نقشه به اشتراک گذاشتم. لطفاً پوشش این نقطه را بررسی کن.');
    return true;
  }, [location, call, sendMessage]);

  const recheckCoverage = useCallback(() => {
    if (!location) return false;
    coverageRequest.current += 1;
    setCoverageLoading(true);
    setCoverage(null);
    setCoverageError(null);
    setCoverageRefresh((value) => value + 1);
    return true;
  }, [location]);

  return {
    messages, location, busy, ready, error,
    setLocation, sendMessage,
    coverageLoading, coverage, coverageError, recheckCoverage,
    clearError: () => setError(null),
    registrationUrl,
    // Phase 3
    callState: call.state, call,
    startCall:     call.startCall,
    endCall:       call.endCall,
    shareLocation,
  };
}
